import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import { MdAttachMoney, MdPerson, MdCreditCard } from "react-icons/md";
import { themeOrange } from "../../utils/colorTheme";
import CustomSelectBox from "../../component/common/selectComp/CustomSelectBox";
import CustomButton from "../../component/common/Button/Button";
import CustomTextArea from "../../component/common/CustomTextArea/CustomTextArea";
import { get_users_list_by_role } from "../../services/DashboardUsers";
import { apiHandle } from "../../config/apiHandle/apiHandle";
import {
  success_toast_message,
  error_toast_message,
} from "../../utils/toast_message";

const ChargeCustomer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    // Fetch customers (role 0)
    dispatch(get_users_list_by_role(0))
      .unwrap()
      .then((res) => {
        console.log("Customers response:", res);
        const users = res.users || res.data || res || [];
        if (Array.isArray(users)) {
          // Filter only customers with cards
          const customersWithCards = users.filter(
            (u) => u.stripe_payment_method_id && u.stripe_customer_id
          );
          setCustomers(customersWithCards);
        }
        setLoadingCustomers(false);
      })
      .catch((err) => {
        console.error("Failed to load customers:", err);
        error_toast_message("Failed to load customers");
        setLoadingCustomers(false);
      });
  }, [dispatch]);

  // Format customers for select dropdown
  const customerOptions = customers.map((customer) => ({
    value: customer.id,
    label: `${customer.first_name || ""} ${customer.last_name || ""} - ${customer.email || ""}`.trim(),
    customer: customer,
  }));

  const handleSubmit = async () => {
    // Validation
    if (!selectedCustomer) {
      error_toast_message("Please select a customer");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      error_toast_message("Please enter a valid amount");
      return;
    }
    if (!reason.trim()) {
      error_toast_message("Please enter a reason for this charge");
      return;
    }

    setSubmitting(true);

    try {
      console.log("Charging customer:", {
        user_id: selectedCustomer.value,
        amount: parseFloat(amount),
        reason: reason.trim(),
      });

      const response = await apiHandle.post("/admin/stripe/charge-customer", {
        user_id: selectedCustomer.value,
        amount: parseFloat(amount),
        reason: reason.trim(),
      });

      console.log("Charge response:", response.data);

      if (response.data.status) {
        success_toast_message(response.data.message || "Customer charged successfully!");
        // Reset form
        setSelectedCustomer(null);
        setAmount("");
        setReason("");
      } else {
        error_toast_message(response.data.message || "Failed to charge customer");
      }
    } catch (error) {
      console.error("Charge error:", error);
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to charge customer";
      error_toast_message(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        padding: { xs: 2, sm: 3 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        my={1}
      >
        <Typography
          sx={{
            fontSize: { xs: "20px", sm: "24px" },
            fontWeight: "700",
            color: "#333",
          }}
        >
          Charge Customer
        </Typography>
      </Stack>
      <Divider />

      <Container
        sx={{
          maxWidth: { xl: "lg", lg: "md", md: "md", sm: "sm", xs: "xs" },
          mt: 3,
        }}
      >
        <Stack
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: { xs: 2, sm: 4 },
            boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          }}
          gap={3}
        >
          {/* Info Alert */}
          <Alert severity="info" icon={<MdCreditCard size={24} />}>
            Charge any amount to a customer's saved card. Only customers with cards on file are shown ({customers.length} available).
          </Alert>

          {customers.length === 0 && !loadingCustomers && (
            <Alert severity="warning">
              No customers with saved payment cards found. Customers need to add a card before they can be charged.
            </Alert>
          )}

          {/* Select Customer */}
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              <MdPerson size={20} color={themeOrange} />
              <Typography fontWeight="600">Select Customer</Typography>
            </Stack>
            <CustomSelectBox
              options={customerOptions}
              value={selectedCustomer}
              onChange={setSelectedCustomer}
              placeholder="Search and select a customer..."
              isSearchable={true}
              isLoading={loadingCustomers}
              noOptionsMessage={() =>
                loadingCustomers
                  ? "Loading customers..."
                  : "No customers with cards on file"
              }
            />
            {selectedCustomer && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  backgroundColor: "#e8f5e9",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  mt: 1,
                }}
              >
                <MdCreditCard size={18} color="#4CAF50" />
                <Typography variant="body2" color="textSecondary">
                  Card on file - ready to charge
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Amount */}
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              <MdAttachMoney size={20} color={themeOrange} />
              <Typography fontWeight="600">Amount (CAD)</Typography>
            </Stack>
            <TextField
              fullWidth
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                min: "0.01",
                step: "0.01",
              }}
            />
          </Stack>

          {/* Reason */}
          <Stack gap={1}>
            <Typography fontWeight="600">Reason for Charge</Typography>
            <CustomTextArea
              placeholder="Enter the reason for this charge (e.g., Additional service, Parts, etc.)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </Stack>

          {/* Summary */}
          {selectedCustomer && amount && parseFloat(amount) > 0 && (
            <Stack
              sx={{
                backgroundColor: "#FFF8F5",
                borderRadius: "12px",
                padding: "16px",
                border: `1px solid ${themeOrange}30`,
              }}
              gap={1}
            >
              <Typography fontWeight="600" color={themeOrange}>
                Charge Summary
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Customer:</Typography>
                <Typography variant="body2" fontWeight="600">
                  {selectedCustomer.customer.first_name} {selectedCustomer.customer.last_name}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Amount to charge:</Typography>
                <Typography variant="body2" fontWeight="700" color={themeOrange}>
                  ${parseFloat(amount).toFixed(2)} CAD
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* Submit Button */}
          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <CustomButton
              onClick={() => navigate("/orders")}
              style={{
                backgroundColor: "#f5f5f5",
                color: "#333",
                padding: "12px 30px",
                borderRadius: "10px",
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSubmit}
              disabled={submitting || !selectedCustomer || !amount || !reason.trim()}
              style={{
                background:
                  "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
                padding: "12px 30px",
                borderRadius: "10px",
                opacity: submitting || !selectedCustomer || !amount || !reason.trim() ? 0.6 : 1,
              }}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                `Charge $${amount || "0.00"}`
              )}
            </CustomButton>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};

export default ChargeCustomer;
